import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Shared schemas
// ============================================================================

/** `YYYY-MM` (e.g. "2026-12"). */
const MonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Month must be formatted as YYYY-MM");

const MonthOverrideStatusSchema = z.enum(["excluded_zero", "risk_factor"]);

/**
 * `value` is the risk-factor percent when status === "risk_factor" (null for
 * "excluded_zero"). Positive = reduction (100 ≡ excluded); negative = uplift
 * for an expected sales increase (e.g. -50 → +50% → ×1.5). The lower bound is
 * capped by the DECIMAL(5,2) column (≈ ×10 uplift) and is easily widened.
 */
export const RISK_PERCENT_MIN = -900;
export const RISK_PERCENT_MAX = 100;
const RiskPercentSchema = z
  .number()
  .min(RISK_PERCENT_MIN)
  .max(RISK_PERCENT_MAX);

export const MonthOverrideSchema = z.object({
  agreementId: z.string().uuid(),
  month: MonthSchema,
  status: MonthOverrideStatusSchema,
  value: RiskPercentSchema.nullable(),
});

// ============================================================================
// List overrides for an agreement
// ============================================================================

export const ListMonthOverridesRequestSchema = z.object({
  agreementId: z.string().uuid("Invalid agreement ID format"),
});

export const ListMonthOverridesResponseSchema = z.object({
  data: z.array(MonthOverrideSchema),
});

// ============================================================================
// Set (upsert) an override for a single (agreement, month)
// ============================================================================

export const SetMonthOverrideRequestSchema = z
  .object({
    agreementId: z.string().uuid("Invalid agreement ID format"),
    month: MonthSchema,
    status: MonthOverrideStatusSchema,
    // Required for risk_factor (0-100); ignored/omitted for excluded_zero.
    value: RiskPercentSchema.optional(),
  })
  .refine((v) => v.status !== "risk_factor" || v.value !== undefined, {
    message: "value (0-100) is required when status is 'risk_factor'",
    path: ["value"],
  });

export const SetMonthOverrideResponseSchema = z.object({
  data: MonthOverrideSchema,
});

// ============================================================================
// Clear an override for a single (agreement, month)
// ============================================================================

export const ClearMonthOverrideRequestSchema = z.object({
  agreementId: z.string().uuid("Invalid agreement ID format"),
  month: MonthSchema,
});

export const ClearMonthOverrideResponseSchema = z.object({
  data: z.object({ cleared: z.boolean() }),
});

// ============================================================================
// Apply the same action to many months at once
// ============================================================================

/**
 * `normal` clears the override for each month; the two status values upsert it.
 */
const BulkModeSchema = z.enum(["normal", "excluded_zero", "risk_factor"]);

export const ApplyBulkMonthOverrideRequestSchema = z
  .object({
    agreementId: z.string().uuid("Invalid agreement ID format"),
    months: z.array(MonthSchema).min(1),
    mode: BulkModeSchema,
    // Required for risk_factor (0-100); ignored otherwise.
    value: RiskPercentSchema.optional(),
  })
  .refine((v) => v.mode !== "risk_factor" || v.value !== undefined, {
    message: "value (0-100) is required when mode is 'risk_factor'",
    path: ["value"],
  });

export const ApplyBulkMonthOverrideResponseSchema = z.object({
  data: z.object({ applied: z.number() }),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const monthOverrideContracts = {
  list: oc
    .input(ListMonthOverridesRequestSchema)
    .output(ListMonthOverridesResponseSchema)
    .route({
      method: "GET",
      summary: "List rebate forecast month overrides",
      description:
        "Lists a rebate agreement's per-month forecast overrides: months excluded as zero and months with a risk-factor reduction or uplift percent.",
    }),

  set: oc
    .input(SetMonthOverrideRequestSchema)
    .output(SetMonthOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Set a rebate forecast month override",
      description:
        "Marks one agreement month as excluded (zero) or applies a risk-factor percent (reduction, or negative for uplift) that the forecast uses instead of projecting normally.",
    }),

  clear: oc
    .input(ClearMonthOverrideRequestSchema)
    .output(ClearMonthOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Clear a rebate forecast month override",
      description:
        "Removes the forecast override for one agreement month so it is projected normally again.",
    }),

  applyBulk: oc
    .input(ApplyBulkMonthOverrideRequestSchema)
    .output(ApplyBulkMonthOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Set forecast overrides for many months",
      description:
        "Applies one action to many months of a rebate agreement: clear the override, exclude as zero, or apply a risk-factor percent.",
    }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type ListMonthOverridesRequest = z.infer<
  typeof ListMonthOverridesRequestSchema
>;
export type ListMonthOverridesResponse = z.infer<
  typeof ListMonthOverridesResponseSchema
>;
export type SetMonthOverrideRequest = z.infer<
  typeof SetMonthOverrideRequestSchema
>;
export type SetMonthOverrideResponse = z.infer<
  typeof SetMonthOverrideResponseSchema
>;
export type ClearMonthOverrideRequest = z.infer<
  typeof ClearMonthOverrideRequestSchema
>;
export type ClearMonthOverrideResponse = z.infer<
  typeof ClearMonthOverrideResponseSchema
>;
export type ApplyBulkMonthOverrideRequest = z.infer<
  typeof ApplyBulkMonthOverrideRequestSchema
>;
export type ApplyBulkMonthOverrideResponse = z.infer<
  typeof ApplyBulkMonthOverrideResponseSchema
>;
